import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronDown, Star } from 'lucide-react';
import { useSite } from '@/context/SiteContext';
import { mediaUrl } from '@/lib/api';
import { imgProps, srcSetFor, variantUrl, isUpload } from '@/lib/img';
import { reviewStats } from '@/lib/seo';
import { orderedGallerySlides } from '@/lib/heroOrder';
import { useLinkedPhotos } from './LinkedMedia';

export const Hero = () => {
  const { site } = useSite();
  const hero = site?.hero || {};
  const reduce = useReducedMotion();
  // Slides: the admin's own uploads (max 8), or — when linked — the Gallery photos (optionally one category), max 50.
  const fromGallery = hero.source === 'gallery';
  const gallery = site?.gallery || [];
  const gallerySlides = fromGallery
    ? orderedGallerySlides(hero, gallery).map((g) => ({ id: g.id, url: g.url, caption: g.caption || '', alt: g.alt || '' }))
    : [];
  // Own uploads are capped at 8; Gallery-linked slides may run up to 50.
  const slides = fromGallery && gallerySlides.length ? gallerySlides : (hero.slides || []).slice(0, 8);
  // Approved guest reviews -> small gold badge next to the hero buttons
  // One source of truth (lib/seo reviewStats) shared with the Reviews section and the Resort JSON-LD
  const { count: reviewCount, avgLabel: reviewAvg } = reviewStats(site?.reviews || []);
  const [idx, setIdx] = useState(0);
  useEffect(() => { setIdx(0); }, [slides.length, hero.source]);
  const isVideo = hero.mode === 'video' && hero.video_url;

  useEffect(() => {
    if (isVideo || slides.length < 2 || reduce) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), hero.interval_ms || 6500);
    return () => clearInterval(t);
  }, [isVideo, slides.length, hero.interval_ms, reduce]);

  useEffect(() => {
    // Preload next slide for seamless fade
    if (slides.length > 1) {
      const next = new Image();
      next.src = mediaUrl(slides[(idx + 1) % slides.length].url);
    }
  }, [idx, slides]);

  const reserveUrl = site?.settings?.reserve_url || '#';
  const siteName = site?.settings?.site_name || 'Bukit Ancak Lembongan Resort';
  const slideAlt = (sl) => sl.alt || sl.caption || `${siteName} — hillside view over the Yellow Bridge, Nusa Lembongan`;
  const heroSizes = '100vw';
  const first = slides[0];

  return (
    <section className="relative h-[100svh] min-h-[560px] w-full overflow-hidden bg-[hsl(var(--brand-navy))] text-white" data-testid="home-hero">
      {/* LCP: preload the first hero photo (WebP variants when it is one of our uploads) */}
      {!isVideo && first?.url && (
        <>
          {isUpload(first.url)
            ? <link rel="preload" as="image" href={variantUrl(first.url, 1280)} imageSrcSet={srcSetFor(first.url)} imageSizes={heroSizes} fetchPriority="high" />
            : <link rel="preload" as="image" href={mediaUrl(first.url)} fetchPriority="high" />}
        </>
      )}
      {isVideo ? (
        <div className="hero-slide">
          <video
            src={mediaUrl(hero.video_url)}
            poster={hero.poster_url ? mediaUrl(hero.poster_url) : undefined}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            data-testid="home-hero-video"
          />
        </div>
      ) : (
        <AnimatePresence initial={false}>
          {slides.length > 0 && (
            <motion.div
              key={slides[idx].id || idx}
              className="hero-slide"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.6, ease: 'easeInOut' }}
              data-testid="home-hero-slide"
            >
              <img {...imgProps(slides[idx].url, { sizes: heroSizes })} alt={slideAlt(slides[idx])} loading={idx === 0 ? 'eager' : 'lazy'} fetchPriority={idx === 0 ? 'high' : undefined} decoding={idx === 0 ? 'sync' : 'async'} className={reduce ? '' : 'kenburns'} />
            </motion.div>
          )}
        </AnimatePresence>
      )}

      <div className="absolute inset-0 hero-overlay" />
      <div className="absolute inset-0 noise" />

      {hero.show_text === false && <h1 className="sr-only">{hero.headline || siteName}</h1>}
      {hero.show_text !== false && (
        <div className="relative z-10 h-full container-x flex flex-col justify-end pb-24 sm:pb-28">
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.3 }} className="eyebrow text-white/75">
            {hero.label}
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.1, delay: 0.45 }} className="font-display text-4xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight mt-4 max-w-4xl">
            {hero.headline}
          </motion.h1>
          {hero.subheadline && (
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.1, delay: 0.6 }} className="mt-5 max-w-xl text-[15px] sm:text-lg text-white/85 leading-relaxed">
              {hero.subheadline}
            </motion.p>
          )}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.8 }} className="mt-8 flex flex-col sm:flex-row sm:items-center gap-3">
            <a href={reserveUrl} target="_blank" rel="noopener noreferrer" className="btn-gold" data-testid="home-hero-reserve-button">Reserve</a>
            <Link to="/stay" className="btn-ghost-dark" data-testid="home-hero-explore-stay-link">Explore Stay</Link>
            {reviewCount > 0 && (
              <a
                href="#reviews"
                onClick={(e) => { e.preventDefault(); document.getElementById('reviews')?.scrollIntoView({ behavior: 'smooth' }); }}
                className="inline-flex items-center gap-2 self-start sm:self-auto sm:ml-2 rounded-full border border-[hsl(var(--brand-gold))]/50 bg-black/25 backdrop-blur-[6px] px-3.5 py-2 text-[hsl(var(--brand-gold))] hover:bg-black/40 transition-colors"
                data-testid="home-hero-review-badge"
                aria-label={`Rated ${reviewAvg} out of 5 from ${reviewCount} guest reviews`}
              >
                <Star className="h-3.5 w-3.5 fill-current" />
                <span className="text-sm font-medium tabular-nums">{reviewAvg}</span>
                <span className="text-[10px] tracking-[0.18em] uppercase text-white/80">{reviewCount} guest review{reviewCount === 1 ? '' : 's'}</span>
              </a>
            )}
          </motion.div>
        </div>
      )}

      {!isVideo && slides.length > 1 && (
        slides.length <= 10 ? (
          <div className="absolute z-10 bottom-8 left-5 sm:left-8 lg:left-10 flex gap-2" data-testid="home-hero-dots">
            {slides.map((s, i) => (
              <button
                key={s.id || i}
                type="button"
                onClick={() => setIdx(i)}
                aria-label={`Go to slide ${i + 1}`}
                className={`h-[3px] rounded-full transition-[width,background-color] duration-500 ${i === idx ? 'w-10 bg-white' : 'w-5 bg-white/40 hover:bg-white/70'}`}
              />
            ))}
          </div>
        ) : (
          // Many Gallery-linked slides: a slim progress bar + counter instead of dozens of dots
          <div className="absolute z-10 bottom-8 left-5 sm:left-8 lg:left-10 flex items-center gap-3" data-testid="home-hero-counter">
            <span className="relative block h-[3px] w-24 rounded-full bg-white/30 overflow-hidden">
              <span className="absolute inset-y-0 left-0 bg-white rounded-full transition-[width] duration-500" style={{ width: `${((idx + 1) / slides.length) * 100}%` }} />
            </span>
            <span className="text-[11px] tracking-[0.2em] text-white/80 tabular-nums">{String(idx + 1).padStart(2, '0')} / {String(slides.length).padStart(2, '0')}</span>
          </div>
        )
      )}

      <motion.div animate={reduce ? {} : { y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 2.2, ease: 'easeInOut' }} className="hidden sm:block absolute z-10 bottom-8 left-1/2 -translate-x-1/2 text-white/60" aria-hidden="true">
        <ChevronDown className="h-5 w-5" />
      </motion.div>
    </section>
  );
};

/** Smaller editorial header for inner pages. */
export const PageHero = ({ image, source = '', label, title, intro, dark = true }) => {
  const linked = useLinkedPhotos(source);
  const heroImage = (source && linked[0]) || image;
  if (!heroImage) {
    return (
      <section className="pt-36 sm:pt-44 pb-10 sm:pb-14 container-x" data-testid="page-hero">
        {label && <p className="eyebrow text-[hsl(var(--brand-teal))]">{label}</p>}
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4">{title}</h1>
        {intro && <p className="mt-5 max-w-xl text-base sm:text-lg text-foreground/70 leading-relaxed">{intro}</p>}
      </section>
    );
  }
  return (
    <section className="relative h-[70svh] min-h-[460px] overflow-hidden bg-[hsl(var(--brand-navy))] text-white" data-testid="page-hero" data-source={source && linked[0] ? source : 'custom'}>
      {isUpload(heroImage) && (
        <link rel="preload" as="image" href={variantUrl(heroImage, 1280)} imageSrcSet={srcSetFor(heroImage)} imageSizes="100vw" fetchPriority="high" />
      )}
      <motion.img key={heroImage} initial={{ scale: 1.08 }} animate={{ scale: 1 }} transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }} {...imgProps(heroImage, { sizes: '100vw' })} alt="" role="presentation" fetchPriority="high" decoding="sync" className="absolute inset-0 h-full w-full object-cover" data-testid="page-hero-image" />
      <div className="absolute inset-0 hero-overlay" />
      <div className="relative z-10 h-full container-x flex flex-col justify-end pb-14 sm:pb-20">
        {label && <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.2 }} className="eyebrow text-white/75">{label}</motion.p>}
        <motion.h1 initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.35 }} className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4">{title}</motion.h1>
        {intro && <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.5 }} className="mt-5 max-w-xl text-base sm:text-lg text-white/85 leading-relaxed">{intro}</motion.p>}
      </div>
    </section>
  );
};
